import { AnalyticsConfig } from '@24i/nxg-sdk-photon';

import { OneTrust } from '../../../utils/oneTrust';
import { SafeURL } from '../../../utils/safeUrl';
import { AllowTracking, DEFAULT_TTL, ParsedCampaignCookie, SavedCampaign } from './types';
import { getSubdomainIndex } from './setMetaCookie';

export const isConversionParameter = (key: string): boolean =>
    key === 'gclid' || key === 'fbclid' || key === 'fbc' || key.startsWith('utm_');

export const isConversionUrl = (deeplinkUrl: string): boolean =>
    typeof deeplinkUrl === 'string' &&
    Array.from(new SafeURL(deeplinkUrl).searchParams.keys()).some((key) =>
        isConversionParameter(key)
    );

export const extractCampaignTtlFromConfig = (
    analyticsConfig: AnalyticsConfig | undefined
): number => {
    if (analyticsConfig?.name !== '24iQ' || !analyticsConfig.enabled) return 0;
    const campaignTtlSeconds = analyticsConfig.options?.conversionApi?.campaignTtlSeconds;
    if (typeof campaignTtlSeconds !== 'number' || campaignTtlSeconds < 0) return DEFAULT_TTL;
    return campaignTtlSeconds;
};

export const detectAllowTracking = async (): Promise<AllowTracking> => {
    if (!OneTrust) return AllowTracking.IRRELEVANT;
    const consent = await OneTrust.getConsentStatus();
    switch (consent.doNotTrack) {
        case true:
            return AllowTracking.DECLINED;
        case false:
            return AllowTracking.ALLOWED;
        default:
            return AllowTracking.UNKNOWN;
    }
};

export const watchConsentStatus = (callback: () => void): (() => void) => {
    if (!OneTrust) return () => {};
    return OneTrust.watchConsentStatus(callback);
};

export const canSaveCampaign = (allowTracking: AllowTracking): boolean =>
    allowTracking !== AllowTracking.DECLINED;

export const canSendCampaign = (allowTracking: AllowTracking): boolean =>
    allowTracking === AllowTracking.ALLOWED || allowTracking === AllowTracking.IRRELEVANT;

export const isCampaignValid = (campaign: SavedCampaign | undefined): boolean =>
    !!campaign && campaign.expires > Date.now() && canSendCampaign(campaign.allowTracking);

export const createCampaign = (
    url: string,
    cookie: ParsedCampaignCookie | undefined,
    allowTracking: AllowTracking,
    creationTime: number,
    campaignTtl: number
): SavedCampaign => ({
    url,
    cookie: cookie?.serialized || '',
    expires: cookie?.expires ?? creationTime + 1000 * campaignTtl,
    allowTracking,
});

export const createMetaCookie = (
    url: string,
    creationTime: number,
    campaignTtl: number
): ParsedCampaignCookie | undefined => {
    const expires = creationTime + 1000 * campaignTtl;
    const parsedUrl = new SafeURL(url);
    const domain = parsedUrl.hostname;
    const fbclid = parsedUrl.searchParams.get('fbclid') || '';
    const subdomainIndex = getSubdomainIndex(domain);
    const serialized = `fb.${subdomainIndex}.${creationTime}.${fbclid}`;
    return fbclid
        ? {
              expires,
              domain,
              serialized,
          }
        : undefined;
};

export const createMetaCookieFromCampaign = (
    campaign: SavedCampaign
): ParsedCampaignCookie | undefined => {
    const { expires, cookie: serialized, url } = campaign;
    const domain = new SafeURL(url).hostname;
    const hasFbclid = url.includes('fbclid=');
    if (!hasFbclid) return undefined;
    return {
        expires,
        domain,
        serialized,
    };
};
